import React, { useEffect, useState } from "react";
import { Modal, Tag, Button, Row, Col, Divider, Table } from "antd";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { formatDateToDMY, formatTime12Hour, formatEventType, getLangValue } from "../../utils/tools";
import { Icon } from "@iconify/react/dist/iconify.js";
import { api } from "../../utils/apiClient";

const EventViewModal = ({ open, onClose, data, onStatusChange }) => {
    const viewPermissions = useSelector((state) => state.permission.rolePermissions) || [];
    const navigate = useNavigate();
    const [userEvents, setUserEvents] = useState([]);
    const [userEventsLoading, setUserEventsLoading] = useState(false);

    useEffect(() => {
        if (!open || !data?.user?.id) {
            setUserEvents([]);
            return;
        }
        const fetchUserEvents = async () => {
            try {
                setUserEventsLoading(true);
                const response = await api.get("/events", {
                    params: { user_id: data.user.id, limit: 50 },
                });
                setUserEvents(response.data.data || []);
            } catch {
                setUserEvents([]);
            } finally {
                setUserEventsLoading(false);
            }
        };
        fetchUserEvents();
    }, [open, data?.user?.id]);

    if (!data) return null;


    const getStatusColor = (status) => {
        switch (status) {
            case "accepted":
                return "green";
            case "rejected":
                return "red";
            case "new":
            default:
                return "blue";
        }
    };

    const Item = ({ label, value }) => (
        <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: "#888", fontWeight: 500 }}>
                {label}
            </div>
            <div style={{ fontSize: 15, fontWeight: 500, wordBreak: "break-word" }}>
                {value || "-"}
            </div>
        </div>
    );

    return (
        <Modal
            title="Event Details"
            open={open}
            onCancel={onClose}
            width={700}
            centered
            footer={
                viewPermissions.includes("event.edit") ? (
                    [
                        <Button
                            key="reject"
                            danger
                            disabled={data.status === "rejected"}
                            onClick={() => onStatusChange(data.id, "rejected")}
                        >
                            Reject
                        </Button>,
                        <Button
                            key="accept"
                            type="primary"
                            disabled={data.status !== "new"}
                            onClick={() => onStatusChange(data.id, "accepted")}
                        >
                            Accept
                        </Button>,
                    ]
                ) : null
            }
        >
            <Row gutter={[16, 10]}>

                {/* <Col xs={24} sm={12}>
                    <Item label="Diocese" value={data.parish?.diocese?.name} />
                </Col>

                <Col xs={24} sm={12}>
                    <Item label="Parish" value={data.parish?.parish_name} />
                </Col> */}

                <Col xs={24} sm={12}>
                    <Item
                        label="Event Title"
                        value={formatEventType(data.event_type?.name ?? data.event_type_others)}
                    />
                </Col>

                <Col xs={24} sm={12}>
                    <Item label="Church Name" value={getLangValue(data.church?.name)} />
                </Col>

                <Col xs={24} sm={12}>
                    <Item label="Latitude" value={data.church?.latitude} />
                </Col>

                <Col xs={24} sm={12}>
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 13, color: "#888", fontWeight: 500 }}>
                            Longitude
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: 15, fontWeight: 500 }}>{data.church?.longitude || "-"}</span>
                            {data.church?.latitude && data.church?.longitude && (
                                <a
                                    href={`https://www.google.com/maps/search/?api=1&query=${data.church.latitude},${data.church.longitude}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ display: 'flex', alignItems: 'center' }}
                                >
                                    <Icon
                                        icon="logos:google-maps"
                                        width="20"
                                        height="20"
                                    />
                                </a>
                            )}
                        </div>
                    </div>
                </Col>

                <Col xs={24} sm={12}>
                    <Item
                        label="Start Date"
                        value={
                            data.start_datetime
                                ? `${formatDateToDMY(data.start_datetime)} ${formatTime12Hour(data.start_datetime)}`
                                : "-"
                        }
                    />
                </Col>


                <Col xs={24} sm={12}>
                    <Item
                        label="End Date"
                        value={
                            data.end_datetime
                                ? `${formatDateToDMY(data.end_datetime)} ${formatTime12Hour(data.end_datetime)}`
                                : "-"
                        }
                    />
                </Col>


                {data.is_recurring && (
                    <Col xs={24} sm={12}>
                        <Item
                            label="Recurring Type"
                            value={
                                (!data.recurring_type || data.recurring_type === "none") && !data.recurring_pattern
                                    ? "-"
                                    : `${formatEventType(data.recurring_type) || "-"} / ${formatEventType(data.recurring_pattern) || "-"}`
                            }
                        />
                    </Col>
                )}



                <Col xs={24} sm={12}>
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 13, color: "#888", fontWeight: 500 }}>
                            Status
                        </div>
                        <Tag color={getStatusColor(data.status)}>
                            {data.status?.toUpperCase()}
                        </Tag>
                    </div>
                </Col>

                <Col xs={24} sm={12}>
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 13, color: "#888", fontWeight: 500 }}>
                            Created By
                        </div>
                        <Tag color="gray">
                            {data.created_by?.type?.toUpperCase()}
                        </Tag>{" "}
                        {data.created_by?.name}
                    </div>
                </Col>

                <Col xs={24} sm={12}>
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 13, color: "#888", fontWeight: 500 }}>
                            Updated By
                        </div>
                        <Tag color="gray">
                            {data.updated_by_admin?.type?.toUpperCase()}
                        </Tag>{" "}
                        {data.updated_by_admin?.name}
                    </div>
                </Col>

                <Col xs={24} sm={12}>
                    <Item label="User Email" value={data.user?.email} />
                </Col>

                <Col xs={24} sm={12}>
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 13, color: "#888", fontWeight: 500 }}>
                            User Event Count
                        </div>
                        <Tag color="blue">
                            {data.user?.festivals_count ?? 0}
                        </Tag>
                    </div>
                </Col>

                {data.user?.id && (
                    <Col xs={24}>
                        <div style={{ fontSize: 13, color: "#888", fontWeight: 500, marginBottom: 8 }}>
                            User Event List
                        </div>
                        <Table
                            size="small"
                            loading={userEventsLoading}
                            dataSource={userEvents}
                            rowKey="id"
                            pagination={{ pageSize: 5, size: "small" }}
                            columns={[
                                {
                                    title: "Event Title",
                                    dataIndex: "event_title",
                                    key: "event_title",
                                    render: (val) => getLangValue(val) || "-",
                                },
                                {
                                    title: "Church Name",
                                    key: "church_name",
                                    render: (_, row) => getLangValue(row.church?.name) || "-",
                                },
                                {
                                    title: "Start Date",
                                    key: "start_datetime",
                                    render: (_, row) =>
                                        row.start_datetime
                                            ? `${formatDateToDMY(row.start_datetime)} ${formatTime12Hour(row.start_datetime)}`
                                            : "-",
                                },
                                {
                                    title: "Status",
                                    dataIndex: "status",
                                    key: "status",
                                    render: (status) => (
                                        <Tag color={status === "accepted" ? "green" : status === "rejected" ? "red" : "blue"}>
                                            {status?.toUpperCase()}
                                        </Tag>
                                    ),
                                },
                                {
                                    title: "Action",
                                    key: "action",
                                    render: (_, row) => (
                                        <button
                                            className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                            onClick={() => navigate(`/events-form/${row.id}`)}
                                        >
                                            <Icon icon="lucide:edit" />
                                        </button>
                                    ),
                                },
                            ]}
                        />
                    </Col>
                )}

            </Row>

            <Divider />

            <div>
                <div style={{ fontSize: 13, color: "#888", fontWeight: 500 }}>
                    Details
                </div>
                <div style={{ fontSize: 15 }}>
                    {getLangValue(data?.details) || "-"}
                </div>
            </div>

        </Modal>
    );
};

export default EventViewModal;